import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import AxiosService from "../../services/AxiosHelper";

const baseURL = process.env.REACT_APP_BASEURL;

export const getCustomersByBusiness = createAsyncThunk(
  "customer/getCustomersByBusiness",
  async (businessId, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/customer/business/${businessId}`,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch customers");
    }
  }
);

export const getCustomerDetail = createAsyncThunk(
  "customer/getDetail",
  async ({ customerId, businessId }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: baseURL + `/customer/${customerId}/business/${businessId}`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch details");
    }
  }
);

export const addCustomer = createAsyncThunk(
  "customer/addCustomer",
  async (values, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + `/customer`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to add customer");
    }
  }
);
export const updateCustomer = createAsyncThunk(
  "customer/updateCustomer",
  async (values, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "put",
        url: baseURL + `/customer/${values.id}`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Update failed");
    }
  }
);
export const deleteCustomer = createAsyncThunk(
  "customer/deleteCustomer",
  async (id, { rejectWithValue }) => {
    try {
      await AxiosService.AxiostRequest({
        method: "delete",
        url: baseURL + `/customer/${id}`,
      });
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete customer");
    }
  }
);

export const fetchCustomerInvoices = createAsyncThunk(
  "customer/fetchCustomerInvoices",
  async ({ customerId, businessId }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: baseURL + `/customer/${customerId}/business/${businessId}/invoices`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch invoices");
    }
  }
);

export const fetchCustomerShipments = createAsyncThunk(
  "customer/fetchCustomerShipments",
  async ({ customerId, businessId }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: baseURL + `/customer/${customerId}/business/${businessId}/shipments`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch shipments");
    }
  }
);

const customerSlice = createSlice({

  name: "customer",
  initialState: {
    customerList: [],
    loading: false,
    customerDetail: null,
    customerInvoices: [],
    customerShipments: [],
    relatedLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCustomersByBusiness.fulfilled, (state, action) => {
      state.customerList = action.payload || [];
      state.loading = false;
    });
    builder.addCase(getCustomerDetail.fulfilled, (state, action) => {
      state.customerDetail = action.payload;
      state.loading = false;
    });
    builder.addCase(addCustomer.fulfilled, (state, action) => {
      state.customerList.unshift(action.payload);
      state.loading = false;
      toast.success("Customer added successfully");
    });
    builder.addCase(updateCustomer.fulfilled, (state, { payload }) => {
      state.customerList = state.customerList.map((customer) =>
        customer._id === payload._id ? payload : customer
      );
      toast.success("Customer successfully updated.");
      state.loading = false;
    });
    builder.addCase(deleteCustomer.fulfilled, (state, { payload }) => {
      state.customerList = state.customerList.filter(
        (customer) => customer._id !== payload
      );
      state.loading = false;
      toast.success("Customer deleted successfully");
    });
    builder.addCase(fetchCustomerInvoices.fulfilled, (state, action) => {
      state.customerInvoices = action.payload || [];
      state.relatedLoading = false;
    });
    builder.addCase(fetchCustomerShipments.fulfilled, (state, action) => {
      state.customerShipments = action.payload || [];
      state.relatedLoading = false;
    });

    // General Matchers
    builder.addMatcher(
      (action) => action.type.startsWith("customer/") && action.type.endsWith("/pending"),
      (state) => { state.loading = true; }
    );
    builder.addMatcher(
      (action) => action.type.startsWith("customer/") && (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
      (state, action) => {
        state.loading = false;
        if (action.type.endsWith("/rejected")) {
          toast.error(action.payload?.message || "Something went wrong");
        }
      }
    );
  },
});
export default customerSlice.reducer;
